import { SessionGrantOrigin } from "types/generated";

export type SpaceDetailsAccessRole = {
  id: string;
  name: string;
  isSystem: boolean;
};

/**
 * Where a space grant lives relative to the queried space:
 * - `resident` — on the queried space itself (shown as "Direct").
 * - `propagated` — on an ancestor, reaching down ("Propagated down").
 * - `inherited` — on an inheriting descendant, reaching up ("Inherited from child space").
 *   Only stack grants can be inherited; users, IdP groups, and API keys map their boolean
 *   `propagated` to resident/propagated.
 */
export enum SpaceDetailsAccessGrantSource {
  RESIDENT = "RESIDENT",
  PROPAGATED = "PROPAGATED",
  INHERITED = "INHERITED",
}

/**
 * How a role binding reaches this space:
 * - `group` — granted through a group (SpaceAccessGrantVia = SpaceAccessGroup); source is not
 *   meaningful here, so it's omitted. Only users can be granted via a group.
 * - `space` — granted on a space (SpaceAccessGrantVia = UserRoleBinding, or the grant's own space
 *   for entities without a grantedVia union); `source` says where that space sits relative to the
 *   queried one.
 */
export type SpaceDetailsAccessGrantedVia =
  | { kind: "group"; id: string; name: string }
  | { kind: "space"; id: string; name: string; source: SpaceDetailsAccessGrantSource };

/** What the role cells read, whatever granted the role. */
export type SpaceDetailsAccessRoleBindingBase = {
  id: string;
  role: SpaceDetailsAccessRole;
};

export type SpaceDetailsAccessRoleBinding = {
  id: string;
  role: SpaceDetailsAccessRole;
  grantedVia: SpaceDetailsAccessGrantedVia;
};

/**
 * A grant held through live sessions. Login flattens the binding, the granting space and the
 * propagation story away, so `origin` is the only attribution that survives.
 */
export type SpaceDetailsAccessSessionRoleBinding = {
  id: string;
  role: SpaceDetailsAccessRole;
  origin: SessionGrantOrigin;
};

export type SpaceDetailsAccessEntity = {
  roleBindings: SpaceDetailsAccessRoleBindingBase[];
};

export type SpaceDetailsAccessGrantedViaEntity = {
  roleBindings: SpaceDetailsAccessRoleBinding[];
};
